import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import ReactGA from "react-ga4";
import CustomNavbar from './components/Navbar';
import Hero from './components/Hero';
import Features from './components/Features';
import HowItWorks from './components/HowItWorks';
import NFCCards from './components/NFCCards';
import SuccessStories from './components/SuccessStories';
import AppDownload from './components/AppDownload';
import Testimonials from './components/Testimonials';
import FAQ from './components/Faq';
import CTA from './components/Cta';
import Enterprise from './pages/Enterprise';
import CustomFooter from './components/Footer';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import PersonalCardPage from './pages/PersonalCard';
import BusinessCardPage from './pages/BusinessCard';
import DigitalWallet from './pages/DigitalWallet';
import CustomCards from './pages/CustomCards';
import NFCCardsPage from './pages/NFCCardsPage';
import BlogPage from './pages/BlogPage';
import HelpCenter from './pages/HelpCenter';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import { useSEO } from '../src/hooks/useSEO';

import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import CartPage from './pages/CartPage';
import AccountPage from './pages/AccountPage';
import PaymentPage from './pages/PaymentPage';
import ConfirmationPage from './pages/ConfirmationPage';
import ChangePasswordPage from './pages/ChangePasswordPage';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { LanguageProvider } from './contexts/LanguageContext';
import TranslationWrapper from './components/TranslationWrapper';

import ProtectedRoute from './components/ProtectedRoute';
import AuthLoadingSpinner from './components/AuthLoadingSpinner';

import { initSmoothScrolling, scrollToTop } from './utils/scrollHandlers';

import WhatIsNFCTechnology from './pages/blog/WhatIsNFCTechnology';
import HowNFCBusinessCardsWork from './pages/blog/HowNFCBusinessCardsWork';
import NFCvsQRCodes from './pages/blog/NFCvsQRCodes';
import EnvironmentalImpactDigitalCards from './pages/blog/EnvironmentalImpactDigitalCards';
import CreativeWaysUseNFCCard from './pages/blog/CreativeWaysUseNFCCard';
import SettingUpDigitalBusinessCard from './pages/blog/SettingUpDigitalBusinessCard';
import FutureOfNetworking from './pages/blog/FutureOfNetworking';
import TrackNetworkingROI from './pages/blog/TrackNetworkingROI';
import NFCSecurity from './pages/blog/NFCSecurity';

ReactGA.initialize("G-3EHDJD8XEC");

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-6">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-2xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    try {
      window.scrollTo(0, 0);
      setTimeout(() => {
        if (window.scrollY > 0) {
          window.scrollTo(0, 0);
        }
      }, 100);
    } catch (error) { }
  }, [pathname]);

  return null;
}

function GoogleAnalytics() {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
  }, [location]);

  return null;
}



function HomePage() {
  useSEO({
    title: "AM CARDMATE - Digital Business Cards & NFC Solutions",
    description: "Create stunning digital business cards with NFC technology. Share instantly, track analytics, and go paperless.",
    image: "/assets/networkfuture.jpg",
    keywords: "digital business cards, NFC cards, contactless sharing"
  });

  useEffect(() => {
    
    window._linkedin_partner_id = "9032265";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(window._linkedin_partner_id);

 
    if (!window.lintrk) {
      window.lintrk = function(a, b) {
        window.lintrk.q.push([a, b]);
      };
      window.lintrk.q = [];
    }

  
    const linkedInScript = document.createElement('script');
    linkedInScript.type = 'text/javascript';
    linkedInScript.async = true;
    linkedInScript.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    document.head.appendChild(linkedInScript);


    const noscriptImg = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.alt = '';
    img.src = 'https://px.ads.linkedin.com/collect/?pid=9032265&fmt=gif';
    noscriptImg.appendChild(img);
    document.body.appendChild(noscriptImg);

   
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "AM CARDMATE",
        "url": "https://amcardmate.com",
        "logo": "https://amcardmate.com/assets/logo.svg",
        "description": "Digital business cards and NFC solutions for modern professionals",
        "sameAs": [
          "https://twitter.com/amcardmate",
          "https://linkedin.com/company/amcardmate",
          "https://instagram.com/amcardmate"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+44-XXX-XXXX",
          "contactType": "customer service",
          "areaServed": "GB",
          "availableLanguage": ["en", "pt", "ru", "uk"]
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "url": "https://amcardmate.com",
        "name": "AM CARDMATE",
        "description": "Digital business cards and NFC solutions",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://amcardmate.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "AM CARDMATE",
        "operatingSystem": "iOS, Android",
        "applicationCategory": "BusinessApplication",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "ratingCount": "1247"
        },
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "GBP"
        },
        "featureList": [
          "Digital Business Cards",
          "NFC Technology",
          "Analytics Dashboard",
          "Contact Management",
          "QR Code Generation"
        ]
      }
    ];
    
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

   
    return () => {
  
      const linkedInScripts = document.head.querySelectorAll('script[src*="snap.licdn.com"]');
      linkedInScripts.forEach(script => script.remove());
      
 
      const scripts = document.head.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => script.remove());
      
  
      const noscriptElements = document.body.querySelectorAll('noscript');
      noscriptElements.forEach(elem => {
        if (elem.innerHTML.includes('px.ads.linkedin.com')) {
          elem.remove();
        }
      });
    };
  }, []);

  return (
    <>
      <Hero />
      <Features />
      <HowItWorks />
      <NFCCards />
      <SuccessStories />
      <AppDownload />
      <Testimonials />
      <FAQ />
      <CTA />
    </>
  );
}

function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

function AppContent() {
  const { initializing, profileLoading, isAuthenticated, isReady } = useAuth();
  const isOnline = useNetworkStatus();

  useEffect(() => {
    if (!isOnline) { }
  }, [isOnline]);

  if (initializing && isAuthenticated()) {
    return (
      <AuthLoadingSpinner
        message="Initializing your session..."
      />
    );
  }

  if (isAuthenticated() && profileLoading && !initializing) {
    return (
      <AuthLoadingSpinner
        message="Loading your profile..."
      />
    );
  }

  return (
    <TranslationWrapper>
      <div className="App antialiased">
        <GoogleAnalytics />

        {!isOnline && (
          <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-white text-center py-2 px-4 z-50">
            <span className="text-sm font-medium">
              ⚠️ You're currently offline. Some features may not work properly.
            </span>
          </div>
        )}

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#333',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              borderRadius: '12px',
              padding: '16px',
              maxWidth: '400px',
            },
            success: {
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
            },
            loading: {
              iconTheme: {
                primary: '#3B82F6',
                secondary: '#fff',
              },
            },
          }}
        />

        <CustomNavbar />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/personal-card" element={<PersonalCardPage />} />
          <Route path="/business-card" element={<BusinessCardPage />} />
          <Route path="/digital-wallet" element={<DigitalWallet />} />
          <Route path="/enterprise" element={<Enterprise />} />
          <Route path="/custom-cards" element={<CustomCards />} />
          <Route path="/nfc-cards" element={<NFCCardsPage />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />

          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/payment-confirmation" element={<ConfirmationPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />

          <Route path="/cart" element={<CartPage />} />

          <Route path="/account" element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          } />

          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/what-is-nfc-technology" element={<WhatIsNFCTechnology />} />
          <Route path="/blog/how-nfc-business-cards-work" element={<HowNFCBusinessCardsWork />} />
          <Route path="/blog/nfc-vs-qr-codes" element={<NFCvsQRCodes />} />
          <Route path="/blog/environmental-impact-digital-cards" element={<EnvironmentalImpactDigitalCards />} />
          <Route path="/blog/creative-ways-use-nfc-card" element={<CreativeWaysUseNFCCard />} />
          <Route path="/blog/setting-up-digital-business-card" element={<SettingUpDigitalBusinessCard />} />
          <Route path="/blog/future-of-networking" element={<FutureOfNetworking />} />
          <Route path="/blog/track-networking-roi" element={<TrackNetworkingROI />} />
          <Route path="/blog/nfc-security" element={<NFCSecurity />} />

          <Route path="*" element={
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
              <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h2>
                <p className="text-gray-600 mb-6">
                  The page you're looking for doesn't exist or has been moved.
                </p>
                <button
                  onClick={() => window.location.href = '/'}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-2xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300"
                >
                  Go to Homepage
                </button>
              </div>
            </div>
          } />
        </Routes>
        <CustomFooter />
      </div>
    </TranslationWrapper>
  );
}

function App() {
  useEffect(() => {
    try {
      initSmoothScrolling();
      scrollToTop();
    } catch (error) { }

    const handleGlobalError = (event) => {
      if (event.error?.message?.includes('Cannot read properties of undefined')) {
        event.preventDefault();
        return;
      }

      if (event.error?.message?.includes('Network Error')) {
        event.preventDefault();
        return;
      }

      if (event.error?.message?.includes('MetaMask extension not found')) {
        event.preventDefault();
        return;
      }
    };

    const handleUnhandledRejection = (event) => {
      if (event.reason?.message?.includes('ERR_INTERNET_DISCONNECTED') ||
        event.reason?.message?.includes('Network Error') ||
        event.reason?.message?.includes('Failed to fetch')) {
        event.preventDefault();
        return;
      }

      if (event.reason?.code?.includes('auth/') ||
        event.reason?.message?.includes('Firebase')) {
        event.preventDefault();
        return;
      }

      if (event.reason?.message?.includes('A listener indicated an asynchronous response')) {
        event.preventDefault();
        return;
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
          <LanguageProvider>
            <Router>
              <ScrollToTop />
              <AppContent />
            </Router>
          </LanguageProvider>
        </CartProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;